// Phase COTTON-RM-002 Part B — typed API client for the /cotton/* endpoints
// shipped in Part A.
import apiClient from '@/shared/api/client'

// ─── Provisional in-premises stock ───────────────────────────────────

export interface ProvisionalStockRow {
  gate_entry_id: string
  inward_no: string
  entry_time: string
  supplier_id: string | null
  supplier_name: string | null
  vehicle_number: string
  po_number: string | null
  do_id: string | null
  gate_status: string
  gross_weight_kg: number | null
  tare_weight_kg: number | null
  net_weight_kg: number | null
  tare_pending: boolean
  estimated_weight_kg: number | null
  weight_confidence: 'EXACT' | 'ESTIMATED' | 'INFERRED' | 'DATA_NOT_AVAILABLE'
  rate_per_kg: number | null
  rate_confidence: 'EXACT' | 'ESTIMATED' | 'INFERRED' | 'DATA_NOT_AVAILABLE'
  insurance_value_estimated: number | null
  value_confidence: 'EXACT' | 'INFERRED' | 'DATA_NOT_AVAILABLE'
  grn_id: string | null
  grn_no: string | null
  grn_status: 'ACCEPTED_PARTIAL' | 'NOT_CREATED'
}

export interface ProvisionalTotals {
  trucks_in_premises: number
  tare_pending: number
  total_provisional_weight_kg: number
  total_provisional_value: number
  data_not_available_count: number
}

// ─── Sampling rules + Quality thresholds ─────────────────────────────

export interface SamplingRule {
  id: string
  rule_code: string
  test_type: 'MOISTURE' | 'HVI'
  default_sample_percent: number
  minimum_sample_percent: number
  maximum_sample_percent: number
  allow_extra_sampling: boolean
  manual_add_allowed: boolean
  active: boolean
  system_seeded: boolean
  admin_modified_at: string | null
}

export interface QualityThreshold {
  id: string
  threshold_code: string
  test_type: string
  parameter_name: string
  min_value: number | null
  max_value: number | null
  warning_min: number | null
  warning_max: number | null
  hold_if_outside: boolean
  reject_if_outside: boolean
  source_priority: number
  supplier_id: string | null
  system_seeded: boolean
  admin_modified_at: string | null
}

// ─── Cost adjustments ────────────────────────────────────────────────

export interface CostAdjustment {
  id: string
  grn_id: string
  lot_id: string | null
  bale_id: string | null
  supplier_id: string | null
  adjustment_type: string
  adjustment_basis: 'FLAT' | 'PER_KG' | 'PER_BALE' | 'PERCENT'
  amount: number
  amount_per_kg: number | null
  quantity_kg: number | null
  sign: number
  calculation_confidence: 'EXACT' | 'ESTIMATED' | 'INFERRED' | 'PROVISIONAL' | 'DATA_NOT_AVAILABLE'
  reason: string
  approval_status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'POSTED'
  finance_posting_status: 'PENDING' | 'STAGED' | 'POSTED'
  downstream_warning: string | null
  approved_at: string | null
  created_at: string
}

export interface GrnLandedSummary {
  grn_id: string
  grn_no: string
  base_invoice_amount: number | null
  approved_adjustment_total: number
  pending_adjustment_total: number
  current_avg_bale_cost_per_kg: number | null
  adjustments: CostAdjustment[]
}

// ─── Mixing readiness ────────────────────────────────────────────────

export interface BaleReadiness {
  bale_id: string
  bale_no: string
  ready: boolean
  blocking_reasons: string[]
}

export interface BlockedBale {
  bale_id: string
  bale_no: string
  grn_id: string | null
  lot_id: string | null
  godown: string | null
  weight_kg: number | null
  effective_cost_per_kg: number | null
  blocking_reasons: string[]
}

export interface ReadyBale {
  bale_id: string
  bale_no: string
  weight_kg: number
  effective_cost_per_kg: number
  godown: string | null
}

// ─── QR ──────────────────────────────────────────────────────────────

export interface QRScanResult {
  id: string
  bale_id: string | null
  qr_code_value: string
  scan_context: string
  scan_datetime: string
  result_status: 'ALLOWED' | 'WARNING' | 'BLOCKED' | 'OVERRIDE_REQUIRED' | 'UNKNOWN_QR'
  error_message: string | null
  bale: {
    bale_no: string
    status: string
    qc_status: string
    bale_stage: string
    godown: string | null
    weight_kg: number | null
    effective_cost_per_kg: number | null
  } | null
}

export interface QRBackfillReport {
  total_bales: number
  qr_already_present: number
  qr_generated: number
  skipped: number
  errors: { bale_id: string; error: string }[]
  dry_run: boolean
}

export interface BaleStatusView {
  legacy: {
    status: string
    qc_status: string
    bale_stage: string
    godown_allocation_status: string | null
  }
  dimensions: {
    physical_status: string
    qc_status: string
    commercial_status: string
  }
}

const P = '/cotton'

export const cottonRmApi = {
  // Provisional stock
  listProvisional: async (params: {
    plant_id?: string; as_of?: string
  } = {}) =>
    (await apiClient.get<ProvisionalStockRow[]>(`${P}/provisional-stock`,
      { params })).data,
  provisionalTotals: async (plant_id?: string) =>
    (await apiClient.get<ProvisionalTotals>(`${P}/provisional-stock/totals`,
      { params: plant_id ? { plant_id } : {} })).data,

  // Sampling rules + thresholds
  samplingRules: async () =>
    (await apiClient.get<SamplingRule[]>(`${P}/sampling-rules`)).data,
  thresholds: async (params: { test_type?: string; parameter?: string } = {}) =>
    (await apiClient.get<QualityThreshold[]>(`${P}/quality-thresholds`, { params })).data,

  // Cost adjustments
  createAdj: async (body: Partial<CostAdjustment> & {
    grn_id: string; adjustment_type: string; amount: number; reason: string
  }) =>
    (await apiClient.post(`${P}/cost-adjustments`, body)).data,
  approveAdj: async (id: string) =>
    (await apiClient.post(`${P}/cost-adjustments/${id}/approve`)).data,
  listAdjForGrn: async (grn_id: string) =>
    (await apiClient.get<CostAdjustment[]>(`${P}/cost-adjustments/grn/${grn_id}`)).data,
  grnLandedSummary: async (grn_id: string) =>
    (await apiClient.get<GrnLandedSummary>(
      `${P}/cost-adjustments/grn/${grn_id}/landed-summary`)).data,
  pendingAdj: async () =>
    (await apiClient.get<CostAdjustment[]>(`${P}/cost-adjustments/pending`)).data,

  // Mixing readiness
  baleReadiness: async (bale_id: string) =>
    (await apiClient.get<BaleReadiness>(`${P}/mixing-readiness/bale/${bale_id}`)).data,
  lotReadiness: async (lot_id: string) =>
    (await apiClient.get(`${P}/mixing-readiness/lot/${lot_id}`)).data,
  readyBales: async (params: {
    plant_id?: string; lot_id?: string; limit?: number
  } = {}) =>
    (await apiClient.get<ReadyBale[]>(`${P}/mixing-readiness/ready`, { params })).data,
  blockedBales: async (params: {
    plant_id?: string; lot_id?: string; grn_id?: string; limit?: number
  } = {}) =>
    (await apiClient.get<BlockedBale[]>(`${P}/mixing-readiness/blocked`,
      { params })).data,

  // Bale status (3-dimension view)
  baleStatus: async (bale_id: string) =>
    (await apiClient.get<BaleStatusView>(`${P}/bales/${bale_id}/status`)).data,

  // QR
  generateQr: async (bale_id: string, force = false) =>
    (await apiClient.post<{ bale_id: string; qr_code_value: string }>(
      `${P}/bales/${bale_id}/qr`, undefined, { params: { force } })).data,
  markPrinted: async (bale_id: string) =>
    (await apiClient.post<{ bale_id: string; ok: boolean }>(
      `${P}/bales/${bale_id}/qr/mark-printed`)).data,
  backfillQr: async (dry_run = false) =>
    (await apiClient.post<QRBackfillReport>(
      `${P}/bales/qr/backfill`, { dry_run })).data,
  scan: async (body: {
    qr_code_value: string
    scan_context: string
    source_module?: string
    device_id?: string
    location_id?: string
    warehouse_code?: string
    override_reason?: string
    override_approved_by?: string
    remarks?: string
  }) =>
    (await apiClient.post<QRScanResult>(`${P}/bales/qr/scan`, body)).data,
  scanHistory: async (bale_id: string, limit = 50) =>
    (await apiClient.get<QRScanResult[]>(
      `${P}/bales/qr/scans/${bale_id}`, { params: { limit } })).data,
}

// Scan contexts surfaced in UIs
export const SCAN_CONTEXTS = [
  'WAREHOUSE_PUTAWAY', 'LOCATION_CHANGE', 'MOISTURE_TEST', 'HVI_TEST',
  'QC_APPROVAL', 'QC_HOLD', 'QC_REJECT', 'MIXING_RESERVATION',
  'BALE_ISSUE', 'MIXING_FEED', 'PRODUCTION_CONSUMPTION', 'STOCK_AUDIT',
  'REPRINT', 'RETURN_TO_SUPPLIER',
] as const
export type ScanContext = typeof SCAN_CONTEXTS[number]

export const ADJUSTMENT_TYPES = [
  'FREIGHT', 'BROKERAGE', 'LOADING_UNLOADING', 'WEIGHBRIDGE_CHARGE',
  'QC_TESTING_CHARGE', 'STORAGE_CHARGE', 'INSURANCE_CHARGE',
  'MOISTURE_DISCOUNT', 'QUALITY_DISCOUNT', 'SUPPLIER_CLAIM', 'OTHER',
] as const

// ─── Phase COTTON-RM-002 Part C — Dashboard / QC reports / Restatements ──

export interface RmDashboardSummary {
  plant_id: string | null
  cards: {
    trucks_in_premises: number
    tare_pending: number
    provisional_kg: number
    provisional_value: number
    data_not_available_value_count: number
    qc_pending: number
    moisture_pending: number
    hvi_pending: number
    qc_hold: number
    accepted_count: number
    accepted_kg: number
    rejected_count: number
    ready_count: number
    ready_kg: number
    reserved_count: number
    reserved_kg: number
    issued_count: number
    issued_kg: number
    consumed_count: number
    consumed_kg: number
    missing_location: number
    missing_cost: number
    qr_missing: number
    qr_printed_not_scanned: number
    pending_cost_adjustments: number
    downstream_cost_warnings: number
    restatement_pending_finance: number
  }
}

export interface QcPendingGrnRow {
  grn_id: string
  grn_no: string
  grn_date: string | null
  supplier_id: string | null
  supplier_name: string | null
  total_bales: number
  moisture_required: number
  moisture_completed: number
  moisture_pending: number
  moisture_pct_avg: number | null
  hvi_required: number
  hvi_completed: number
  hvi_pending: number
  hold_count: number
  approved_count: number
  rejected_count: number
  pending_count: number
  age_days: number | null
  moisture_sampling_pct: number | null
  hvi_sampling_pct: number | null
}

export interface QcPendingReport {
  as_of: string
  overdue_days_threshold: number
  moisture_pending: QcPendingGrnRow[]
  hvi_pending: QcPendingGrnRow[]
  qc_hold: QcPendingGrnRow[]
  approval_pending: QcPendingGrnRow[]
  overdue: QcPendingGrnRow[]
  rejected_return: QcPendingGrnRow[]
  totals: Record<string, number>
}

export interface CostAdjustmentReportRow extends CostAdjustment {
  grn_no: string
  supplier_name: string | null
  signed_amount: number
}

export interface MixingReadinessReport {
  ready_count: number
  blocked_count: number
  reserved_count: number
  issued_count: number
  consumed_count: number
  ready: ReadyBale[]
  blocked: BlockedBale[]
  reserved: { bale_id: string; bale_no: string; weight_kg: number; effective_cost_per_kg: number | null; godown: string | null }[]
  issued: { bale_id: string; bale_no: string; weight_kg: number; effective_cost_per_kg: number | null; godown: string | null }[]
  consumed: { bale_id: string; bale_no: string; weight_kg: number; effective_cost_per_kg: number | null; godown: string | null }[]
  common_blockers: [string, number][]
}

export type RestatementApprovalStatus =
  'DRAFT' | 'PENDING_FINANCE_REVIEW' | 'APPROVED' | 'REJECTED' | 'POSTED' | 'CANCELLED'

export interface CottonCostRestatement {
  id: string
  adjustment_id: string
  grn_id: string
  lot_id: string | null
  bale_id: string | null
  affected_bale_count: number
  affected_issue_slip_count: number
  affected_laydown_count: number
  affected_production_log_count: number
  affected_fg_lot_count: number
  old_total_cost: number
  new_total_cost: number
  difference_amount: number
  reason: string
  approval_status: RestatementApprovalStatus
  finance_posting_status: 'PENDING' | 'STAGED' | 'POSTED'
  debit_ledger: string | null
  credit_ledger: string | null
  requested_at: string
  reviewed_at: string | null
  remarks: string | null
}

export interface RestatementImpactLine {
  bale_id: string
  bale_no: string
  weight_kg: number
  status: string
  old_per_kg: number
  new_per_kg: number
  old_total: number
  new_total: number
  difference: number
}

export interface RestatementImpact extends CottonCostRestatement {
  adjustment_type: string | null
  adjustment_amount: number | null
  adjustment_sign: number | null
  lines: RestatementImpactLine[]
  request_id: string
}

// Extended API surface (Part C)
export const cottonRmApiPartC = {
  dashboard: async (plant_id?: string) =>
    (await apiClient.get<RmDashboardSummary>(`${P}/rm-dashboard/summary`,
      { params: plant_id ? { plant_id } : {} })).data,

  qcPending: async (params: { plant_id?: string; overdue_days?: number } = {}) =>
    (await apiClient.get<QcPendingReport>(`${P}/qc-pending`, { params })).data,

  costAdjReport: async (params: {
    approval_status?: string; finance_status?: string;
    downstream_only?: boolean; limit?: number
  } = {}) =>
    (await apiClient.get<CostAdjustmentReportRow[]>(
      `${P}/cost-adjustments/report`, { params })).data,

  mixingReadiness: async (params: {
    plant_id?: string; lot_id?: string; grn_id?: string
  } = {}) =>
    (await apiClient.get<MixingReadinessReport>(
      `${P}/mixing-readiness/report`, { params })).data,

  listRestatements: async (params: {
    approval_status?: string; grn_id?: string; limit?: number
  } = {}) =>
    (await apiClient.get<CottonCostRestatement[]>(
      `${P}/cost-restatements`, { params })).data,

  createRestatement: async (adjustment_id: string, reason: string) =>
    (await apiClient.post<CottonCostRestatement>(
      `${P}/cost-restatements/from-adjustment/${adjustment_id}`,
      { reason })).data,

  restatementImpact: async (id: string) =>
    (await apiClient.get<RestatementImpact>(
      `${P}/cost-restatements/${id}/impact`)).data,

  approveRestatement: async (id: string, remarks?: string) =>
    (await apiClient.post<CottonCostRestatement>(
      `${P}/cost-restatements/${id}/approve`, { remarks })).data,

  rejectRestatement: async (id: string, remarks: string) =>
    (await apiClient.post<CottonCostRestatement>(
      `${P}/cost-restatements/${id}/reject`, { remarks })).data,

  stageRestatementFinance: async (id: string,
    body: { debit_ledger?: string; credit_ledger?: string } = {}) =>
    (await apiClient.post<CottonCostRestatement>(
      `${P}/cost-restatements/${id}/stage-finance`, body)).data,
}

// ─── Phase COTTON-PARAKH-001 — divergence + Parakh-procured count ────────

export interface ParakhDivergenceRow {
  parameter: string
  mill_avg: number | null
  parakh_avg: number | null
  delta: number | null
  tolerance: number
  divergent: boolean
  verdict: 'DIVERGENT' | 'OK' | 'INSUFFICIENT_DATA'
  message: string
}

export interface GrnDivergence {
  grn_id: string
  grn_no: string
  is_parakh_procured: boolean
  linked_parakh_lot_count: number
  linked_parakh_lots: { id: string; ginner_lot_no: string; test_result: string }[]
  sampled_bale_count: number
  rows: ParakhDivergenceRow[]
  any_divergent: boolean
  qc_decision_status: string | null
}

export const parakhApi = {
  listDivergent: async (plant_id?: string) =>
    (await apiClient.get<GrnDivergence[]>(`${P}/parakh-divergence`,
      { params: plant_id ? { plant_id } : {} })).data,

  parakhInQcCount: async (plant_id?: string) =>
    (await apiClient.get<{ count: number }>(`${P}/parakh-procured-in-qc`,
      { params: plant_id ? { plant_id } : {} })).data,
}
